import { useRequestPatientReinvitation } from '@/hooks/auth/useRequestPatientReinvitation';
import { useResendInviteCountdown } from '@/hooks/escript/useResendInviteCountdown';
import { LOGIN_ROUTE } from '@/routes/route-paths';
import { emitUIEvent } from '@montugroup/data-collection';
import { Divider, Link, LoadingButton, Stack, toast, Typography } from '@montugroup/design-system';
import { styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

const ResendButton = styled(LoadingButton)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('md')]: { width: 'auto', alignSelf: 'flex-start' },
}));

// Matches the per-patient cooldown in circuit-service (PATIENT_REINVITATION_COOLDOWN_SECONDS in
// src/workflows/patient-reinvitation/domain/patient-reinvitation-throttle.ts). Change both together.
const REINVITE_COOLDOWN_SECONDS = 60;

const INVITE_EVENT_DETAILS = {
  screenViewed: { id: 'escript_onboarding_expired_invite', value: 'expired invite screen viewed' },
  requestAccepted: {
    id: 'escript_onboarding_expired_invite_request_accepted',
    value: 'expired invite request accepted',
  },
  error: { id: 'escript_onboarding_expired_invite_error', value: 'expired invite request failed' },
} as const;

const trackInviteEvent = (eventName: keyof typeof INVITE_EVENT_DETAILS) =>
  emitUIEvent(
    {
      component_name: 'expired_invite_screen',
      event_name: eventName,
      ...INVITE_EVENT_DETAILS[eventName],
    },
    [],
  );

export function ExpiredInvite({ onboardKey }: { onboardKey: string }) {
  // Counted rather than flagged so that each accepted request, a resend included, moves focus to the heading again.
  const [acceptedRequestCount, setAcceptedRequestCount] = useState(0);
  // Counted for the same reason: each failed request moves focus back to the button that sent it.
  const [failedRequestCount, setFailedRequestCount] = useState(0);
  const isInviteRequested = acceptedRequestCount > 0;
  const { mutate, isPending } = useRequestPatientReinvitation();
  const { secondsRemaining, startCountdown } = useResendInviteCountdown(REINVITE_COOLDOWN_SECONDS);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Blocks a second click that lands before `isPending` has re-rendered the button as loading.
  const isRequestInFlight = useRef(false);

  useEffect(() => {
    trackInviteEvent('screenViewed');
  }, []);

  useEffect(() => {
    if (acceptedRequestCount > 0) {
      headingRef.current?.focus();
    }
  }, [acceptedRequestCount]);

  useEffect(() => {
    if (failedRequestCount > 0) {
      buttonRef.current?.focus();
    }
  }, [failedRequestCount]);

  const requestInvite = () => {
    if (isRequestInFlight.current || isPending || secondsRemaining > 0) {
      return;
    }
    isRequestInFlight.current = true;
    mutate(
      { body: { onboardKey } },
      {
        onSettled: () => {
          isRequestInFlight.current = false;
        },
        onSuccess: () => {
          setAcceptedRequestCount((count) => count + 1);
          startCountdown();
          trackInviteEvent('requestAccepted');
        },
        onError: () => {
          // An earlier accepted request may still be on its way, so its confirmation stays and only the toast reports
          // this failure.
          setFailedRequestCount((count) => count + 1);
          toast.warn("We couldn't send your invite link. Please try again.");
          trackInviteEvent('error');
        },
      },
    );
  };

  const resendLabel = secondsRemaining > 0 ? `Resend invite in ${secondsRemaining}s` : 'Resend invite';

  return (
    <Stack spacing={4}>
      <Typography
        ref={headingRef}
        component="h2"
        variant="subtitle1"
        fontWeight={600}
        color="primary.main"
        tabIndex={-1}
      >
        {isInviteRequested ? 'Invite request received.' : 'Your invite link has expired.'}
      </Typography>
      <Typography>
        {isInviteRequested
          ? 'If we can resend an invite for this link, a new link is on its way by email or text message.'
          : 'Click the button below to request a new invite link.'}
      </Typography>
      {isInviteRequested ? (
        <>
          <Divider />
          <Typography variant="body2">
            Didn&apos;t get the invite?{' '}
            <LoadingButton
              ref={buttonRef}
              variant="text"
              size="small"
              disabled={secondsRemaining > 0}
              loading={isPending}
              onClick={requestInvite}
              data-tracking-id="escript_onboarding_expired_invite_resend_again"
              sx={{ p: 0, minWidth: 0, textDecoration: 'underline' }}
            >
              {isPending ? 'Requesting invite...' : resendLabel}
            </LoadingButton>
          </Typography>
        </>
      ) : (
        <ResendButton
          ref={buttonRef}
          variant="contained"
          size="large"
          loading={isPending}
          onClick={requestInvite}
          data-tracking-id="escript_onboarding_expired_invite_resend"
        >
          {isPending ? 'Requesting invite...' : 'Resend Invite Link'}
        </ResendButton>
      )}
      <Typography variant="body2">
        Already signed up?{' '}
        <Link component={RouterLink} to={LOGIN_ROUTE} data-tracking-id="escript_onboarding_expired_invite_sign_in">
          Sign in
        </Link>
      </Typography>
    </Stack>
  );
}
